/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Gavel, MapTrifold, ShieldCheck, ChartLineUp, Certificate, FileText,
  Buildings, ArrowRight, Database, Siren, Gauge, Scales, Stack,
} from "@phosphor-icons/react";
import {
  MarketingNav, MarketingFooter, Eyebrow, FeatureCard, KpiTile, BulletList,
  CtaBanner, BTN_GREEN,
} from "./_shared";

export default function ForGovernment() {
  useEffect(() => {
    document.title = "Regulatory Intelligence for Waste Governance · Rescigo Health";
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="min-h-screen bg-white" data-testid="page-for-government">
      <MarketingNav/>

      {/* HERO */}
      <section className="relative pt-36 pb-24 overflow-hidden bg-gradient-to-b from-[#F4F7F2] to-white">
        <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(circle at 80% 20%, rgba(45,90,39,0.08) 0%, transparent 45%)" }}/>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-14 items-center">
          <div>
            <Eyebrow>Government · Public Health Authority</Eyebrow>
            <h1 className="font-heading text-5xl md:text-6xl mt-4 leading-[1.05] text-slate-900">
              Regulatory intelligence for <span className="text-[#2D5A27] italic">smarter</span> waste governance.
            </h1>
            <p className="text-lg text-slate-600 mt-6 leading-relaxed max-w-xl">
              Jurisdiction heat-maps, BMW compliance analytics, chain-of-custody ledgers,
              and court-admissible audit trails — purpose-built for ULBs, pollution control
              boards, and public-health departments.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/#contact" data-testid="gov-cta-demo" className={BTN_GREEN}>
                Request government demo <ArrowRight size={14} weight="bold"/>
              </Link>
              <Link to="/gov/login" data-testid="gov-cta-login"
                    className="inline-flex items-center gap-2 h-12 px-5 rounded-full border border-slate-300 text-slate-800 hover:border-slate-400 text-sm font-medium">
                Officer sign-in
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-5 text-[11px] uppercase tracking-[0.2em] text-slate-500">
              <span className="flex items-center gap-2"><ShieldCheck size={13} weight="fill" className="text-[#2D5A27]"/>ISO 27001</span>
              <span>·</span>
              <span>BMW Rules 2016</span>
              <span>·</span>
              <span>CPCB aligned</span>
            </div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                       transition={{ duration: 0.8 }}
                       className="relative">
            <div className="bg-white rounded-2xl shadow-2xl ring-1 ring-slate-200 p-6 relative">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Kerala · State Dashboard</div>
                  <div className="font-heading text-lg text-slate-900">BMW Compliance · Live</div>
                </div>
                <div className="text-[10px] px-2 py-1 bg-[#D4E0D1] text-[#1A3317] rounded font-semibold uppercase tracking-[0.15em]">98.6%</div>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <MiniStat label="Districts" v="14" sub="/14 live"/>
                <MiniStat label="LSGDs" v="1,034" sub="tracked"/>
                <MiniStat label="Citizens" v="34.8M" sub="covered"/>
              </div>
              <div className="h-40 bg-gradient-to-br from-[#F4F7F2] to-slate-50 rounded-lg relative overflow-hidden">
                <BarChartSVG/>
              </div>
              <div className="mt-3 flex items-center justify-between text-[10px] text-slate-500">
                <span>Updated 47 seconds ago</span>
                <span className="font-mono">v2.1.0</span>
              </div>
            </div>
            <div className="absolute -top-5 -left-5 w-24 h-24 rounded-full bg-[#2D5A27]/10 blur-2xl"/>
            <div className="absolute -bottom-5 -right-5 w-32 h-32 rounded-full bg-[#0A2540]/10 blur-2xl"/>
          </motion.div>
        </div>
      </section>

      {/* CORE CAPABILITIES */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl">
            <Eyebrow>Core capabilities</Eyebrow>
            <h2 className="font-heading text-4xl md:text-5xl mt-4 text-slate-900">
              Built for policy-grade oversight.
            </h2>
            <p className="text-slate-600 mt-4 text-lg leading-relaxed">
              Every transaction, every gram of waste, every officer action — captured,
              verifiable, and exportable as admissible evidence.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5 mt-12">
            <FeatureCard Icon={MapTrifold} title="Jurisdiction Heat-Maps" desc="State · District · LSGD · Ward drill-downs. See compliance gaps in real time across every tier of administration."/>
            <FeatureCard Icon={Siren} title="BMW Compliance Monitoring" desc="Automated tracking of Bio-Medical Waste Rules 2016 indicators — segregation, transport, disposal certification, occupational safety."/>
            <FeatureCard Icon={Scales} title="Legal Audit Trails" desc="Every officer action, status change, and policy override is signed, time-stamped, and exportable as court-ready evidence."/>
            <FeatureCard Icon={Certificate} title="Certificate Verification" desc="Scan-to-verify QR certificates for trained personnel, authorized operators, and compliant facilities — instant authenticity check."/>
            <FeatureCard Icon={ChartLineUp} title="Public Health Dashboards" desc="District-wise indicators: vector-disease risk zones, waste-density mapping, segregation compliance, citizen engagement metrics."/>
            <FeatureCard Icon={Database} title="Circular Economy Metrics" desc="Recycled vs disposed ratios, CO₂ savings, economic value recovered — the full sustainability ledger for policy briefings."/>
          </div>
        </div>
      </section>

      {/* VALUE KPIs */}
      <section className="py-20 bg-[#0A2540] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Eyebrow color="#FFFFFF99">Policy impact at scale</Eyebrow>
          <h2 className="font-heading text-4xl md:text-5xl mt-4 max-w-3xl leading-tight">
            What a fully instrumented jurisdiction looks like.
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
            <GovKpi label="BMW Compliance" v="98.6%" sub="↑ 14 pts YoY"/>
            <GovKpi label="Citizens Covered" v="2.3M" sub="across 38 zones"/>
            <GovKpi label="Audit Events Captured" v="14.7M" sub="in last 12 months"/>
            <GovKpi label="Avg Response Time" v="&lt; 4h" sub="on non-compliance flags"/>
          </div>
        </div>
      </section>

      {/* TWO-COL — HOW IT WORKS */}
      <section className="py-24 bg-[#F7F6F2]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-14 items-start">
          <div>
            <Eyebrow>Officer experience</Eyebrow>
            <h2 className="font-heading text-4xl md:text-5xl mt-4 text-slate-900">One login. Every lever of jurisdiction.</h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Designated officers access a role-scoped console — no operational clutter,
              just the indicators their mandate requires. MFA-enforced, geo-fenced,
              and session-logged.
            </p>
            <div className="mt-6"><BulletList items={[
              "Hierarchical access: State · District · LSGD · Ward",
              "Read-only by default — zero risk of data tampering",
              "One-click PDF reports for weekly & monthly briefings",
              "Policy override workflow with dual-approval & audit",
              "Instant escalation to pollution control board on breach",
            ]}/></div>
          </div>
          <div className="bg-white rounded-2xl shadow-xl ring-1 ring-slate-200 p-8 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#2D5A27]/5 rounded-full blur-3xl"/>
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#2D5A27]">Use case · Ernakulam District</div>
              <h3 className="font-heading text-2xl mt-2 text-slate-900">Real-time BMW non-compliance alert</h3>
              <div className="mt-6 space-y-4">
                <TimelineStep n="01" title="Waste stream mismatch detected" time="14:02 IST"
                               desc="Hospital segregation data diverges from authorized operator intake."/>
                <TimelineStep n="02" title="District health officer notified" time="14:02 IST"
                               desc="Push notification + email with evidence pack."/>
                <TimelineStep n="03" title="Site inspection scheduled" time="14:27 IST"
                               desc="Officer assigns inspection, files audit entry from mobile."/>
                <TimelineStep n="04" title="Audit report generated" time="16:18 IST"
                               desc="Signed PDF with chain-of-custody attached. Filed."/>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CtaBanner
        eyebrow="Deploy jurisdiction monitoring"
        title="Your jurisdiction deserves evidence-grade oversight."
        body="We partner with state pollution control boards, ULBs, and public-health departments. Talk to our deployment architects about a pilot in your district."
        primary={{ label: "Request demo", to: "/#contact", testid: "gov-cta-bottom" }}
        secondary={{ label: "Officer portal", to: "/gov/login" }}
      />

      <MarketingFooter/>
    </div>
  );
}

const MiniStat = ({ label, v, sub }) => (
  <div className="bg-slate-50 rounded-lg px-3 py-2.5">
    <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
    <div className="font-heading text-xl text-slate-900 mt-0.5">{v}</div>
    <div className="text-[10px] text-slate-500">{sub}</div>
  </div>
);

const GovKpi = ({ label, v, sub }) => (
  <div className="bg-white/5 rounded-xl ring-1 ring-white/10 p-5 backdrop-blur-sm">
    <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">{label}</div>
    <div className="font-heading text-3xl md:text-4xl mt-2 text-white" dangerouslySetInnerHTML={{ __html: v }}/>
    <div className="text-[11px] text-white/50 mt-1">{sub}</div>
  </div>
);

const TimelineStep = ({ n, title, time, desc }) => (
  <div className="flex gap-4">
    <div className="flex-none w-9 h-9 rounded-full bg-[#2D5A27]/10 text-[#2D5A27] grid place-items-center font-heading font-bold text-xs">{n}</div>
    <div className="flex-1">
      <div className="flex items-center justify-between">
        <div className="font-medium text-slate-900 text-sm">{title}</div>
        <div className="text-[10px] text-slate-500 font-mono">{time}</div>
      </div>
      <div className="text-xs text-slate-500 mt-1">{desc}</div>
    </div>
  </div>
);

const BarChartSVG = () => {
  const bars = [72, 88, 94, 91, 96, 98, 94, 99];
  return (
    <svg viewBox="0 0 280 140" className="w-full h-full p-3">
      {bars.map((h, i) => (
        <rect key={i} x={i * 34 + 8} y={130 - (h * 1.1)}
              width="24" height={h * 1.1} rx="3"
              fill={i === bars.length - 1 ? "#2D5A27" : "#2D5A2740"}/>
      ))}
      <line x1="0" y1="130" x2="280" y2="130" stroke="#94A3B830" strokeWidth="1"/>
    </svg>
  );
};
